import { siteConfig } from "@/config/site";

export interface Metric {
  label: string;
  value: string;
  delta?: string;
  source?: string;
  captured?: string;
}

export interface PortfolioProject {
  slug: string;
  client: string;
  title: string;
  tagline: string;
  industry: string;
  window: string;
  duration: string;
  country: string;
  platform: string;
  domain: string;
  cover: string;

  coverVariant: string;
  featured: boolean;
  hasCaseStudy: boolean;
  metrics: Metric[];
  services: string[];
  goals: string[];
  challenges: string[];
  solutions: string[];
  deliverables: string[];
  stack: string[];
}

export const PORTFOLIO: PortfolioProject[] = [
  {
    slug: "foreign-affairs-auto",
    client: "Foreign Affairs Auto",
    title: "Technical SEO & Authority Rebuild",
    tagline: "From legacy site debt to 6.1K monthly organic visits and a defensible authority moat.",
    industry: "Automotive",
    window: "Q3 2025 — Q1 2026",
    duration: "6 months",
    country: "USA (Florida)",
    platform: "WordPress / Custom",
    domain: "foreignaffairsauto.com",
    cover: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&q=80&w=1200",

    coverVariant: "ind-automotive",
    featured: true,
    hasCaseStudy: true,
    services: ["Technical SEO", "Digital PR", "Content Strategy", "Local SEO"],
    metrics: [
      { label: "Organic visits", value: "6.1K", delta: "+142%", source: "GA4", captured: "Jan 2026" },
      { label: "Ranking keywords", value: "7K", delta: "+89%", source: "Semrush", captured: "Jan 2026" },
      { label: "Referring domains", value: "911", delta: "+64%", source: "Semrush", captured: "Jan 2026" },
      { label: "Authority Score", value: "29", source: "Semrush" }
    ],
    goals: [
      "Consolidate technical debt from multiple CMS migrations",
      "Recover rankings for core European repair marques",
      "Build a defensible authority moat through digital PR"
    ],
    challenges: [
      "380+ indexable URLs for a 40-page site",
      "Quadruplicate service pages diluting link equity",
      "Failing Core Web Vitals on mobile service pages"
    ],
    solutions: [
      "Hub-and-spoke IA rebuild for marque/service intent",
      "Aggressive URL consolidation and 301 mapping",
      "Founder-led digital PR campaign for trade placements"
    ],
    deliverables: [
      "Technical Fix Log (147 issues)",
      "Information Architecture Map",
      "12 High-Authority PR Placements"
    ],
    stack: ["Screaming Frog", "Semrush", "GSC", "GA4", "SearchAtlas"]
  },
  {
    slug: "lynx-systems",
    client: "Lynx Systems",
    title: "Commercial Security Search Growth",
    tagline: "619K impressions and 2.27K clicks through technical remediation and entity-first content.",
    industry: "Security",
    window: "Q4 2025 — Q2 2026",
    duration: "6 months",
    country: "USA (National)",
    platform: "React / Custom",
    domain: "lynxsystems.com",
    cover: "https://images.unsplash.com/photo-1557597774-9d273605dfa9?auto=format&fit=crop&q=80&w=1200",

    coverVariant: "ind-security-automation",
    featured: true,
    hasCaseStudy: true,
    services: ["Technical SEO", "Content Strategy", "Local SEO", "AEO/GEO"],
    metrics: [
      { label: "GSC Impressions", value: "619K", delta: "+112%", source: "GSC", captured: "Feb 2026" },
      { label: "Organic Clicks", value: "2.27K", delta: "+16%", source: "GSC", captured: "Feb 2026" },
      { label: "Avg. Position", value: "17.3", delta: "Improving", source: "GSC" },
      { label: "Indexable Pages", value: "62", source: "Screaming Frog" }
    ],
    goals: [
      "Fix JavaScript rendering gaps in service tabs",
      "Standardize on-page targeting for commercial intent",
      "Implement structured data for LocalBusiness and Service"
    ],
    challenges: [
      "Core service content hidden from initial HTML payload",
      "Weak internal linking between industries and services",
      "GBP unmanaged for high-value service area"
    ],
    solutions: [
      "Rebuilt tab components for server-side rendering",
      "Rewrote 19 flagship service pages for buyer intent",
      "Deployed LocalBusiness + Service schema sitewide"
    ],
    deliverables: [
      "Technical Fix Log (62 issues)",
      "On-page Content Briefs",
      "GBP Audit & Strategy"
    ],
    stack: ["GSC", "Screaming Frog", "Semrush", "Next.js"]
  },
  {
    slug: "air-tech-pro",
    client: "Air Tech Pro",
    title: "HVAC Local Pack Dominance",
    tagline: "78% click growth in a hyper-competitive local HVAC market through ZIP-level strategy.",
    industry: "HVAC",
    window: "Q1 2026",
    duration: "3 months",
    country: "USA (Texas)",
    platform: "WordPress",
    domain: "airtechpro.com",
    cover: "https://images.unsplash.com/photo-1581094288338-2314dddb7ecc?auto=format&fit=crop&q=80&w=1200",

    coverVariant: "ind-hvac-home-services",
    featured: true,
    hasCaseStudy: false,
    services: ["Local SEO", "GBP Management", "Technical SEO"],
    metrics: [
      { label: "Organic Clicks", value: "+78%", delta: "61 to 109", source: "GSC", captured: "Mar 2026" },
      { label: "GSC Impressions", value: "60.8K", delta: "+8%", source: "GSC", captured: "Mar 2026" },
      { label: "Pack Visibility", value: "Top 3", source: "BrightLocal" }
    ],
    goals: [
      "Win the Local Pack for high-value ZIP codes",
      "Increase mobile click-to-call conversions",
      "Optimize GBP for emergency search intent"
    ],
    challenges: [
      "High competition from national franchise brands",
      "Missing service-area pages for suburban catchments",
      "Stale GBP profile with zero active posts"
    ],
    solutions: [
      "Built unique service-area pages for 12 local cities",
      "Full GBP category and service list overhaul",
      "Implemented mobile-first CRO for emergency leads"
    ],
    deliverables: [
      "Local Search Roadmap",
      "12 Service-Area Pages",
      "GBP Post Cadence"
    ],
    stack: ["BrightLocal", "GSC", "Semrush"]
  },
  {
    slug: "carage-auto",
    client: "Carage Auto",
    title: "AI Search & GEO Strategy",
    tagline: "137 pages cited by ChatGPT and Perplexity through entity-first content architecture.",
    industry: "Automotive",
    window: "Q4 2025 — Q1 2026",
    duration: "4 months",
    country: "USA (Florida)",
    platform: "Custom / React",
    domain: "carageauto.com",
    cover: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80&w=1200",

    coverVariant: "ind-automotive",
    featured: true,
    hasCaseStudy: false,
    services: ["AEO/GEO", "Technical SEO", "Content Strategy"],
    metrics: [
      { label: "AI Cited Pages", value: "137", delta: "+110%", source: "Semrush AI", captured: "Feb 2026" },
      { label: "GEO Visibility", value: "Rank 1", source: "Perplexity" },
      { label: "Keyword Growth", value: "+45%", source: "Semrush" }
    ],
    goals: [
      "Maximize visibility in AI-first search engines",
      "Structure service content for easy extraction",
      "Build topical authority for European diagnostics"
    ],
    challenges: [
      "Content not optimized for retrieval-augmented generation",
      "Weak entity signaling for specific repair expertise",
      "Zero visibility in Perplexity/ChatGPT citations"
    ],
    solutions: [
      "Implemented direct-answer paragraph structures",
      "Deployed advanced entity-linking schema",
      "Built 40+ 'How-To' diagnostic guides"
    ],
    deliverables: [
      "GEO Visibility Audit",
      "Entity Schema Graph",
      "Content Gap Analysis"
    ],
    stack: ["Semrush AI Search", "Perplexity", "GSC"]
  }
];

export const getProject = (slug: string) => PORTFOLIO.find((p) => p.slug === slug);

export const getRelatedProjects = (slug: string, limit = 3) => {
  const current = getProject(slug);
  if (!current) return [];
  return PORTFOLIO.filter((p) => p.slug !== slug && p.industry === current.industry).slice(0, limit);
};
